import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useNavigate,
  useOutletContext,
  Link
} from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import Dashboard from './components/Dashboard';
import MapComponent from './components/MapComponent';
import PersonRemover from './components/PersonRemover';
import ClothingTryOn from './components/ClothingTryOn';
import CreditDisplay from './components/CreditDisplay';
import PaymentModal from './components/PaymentModal';
import AuthComponent from './components/AuthComponent';
import ImageHistory from './components/ImageHistory';
import PrivacyPolicy from './components/PrivacyPolicy';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import PaymentComplete from './components/PaymentComplete';
import GenerationProgress from './components/GenerationProgress';
import InstagramShareTips from './components/InstagramShareTips';
import useScrollIntoView from './components/useScrollIntoView';
import { NotificationProvider, useNotifications } from './components/Notifications';
import { auth } from './firebase';
import { isPreviewMode, previewUser, previewCredits } from './devPreview';
import { useI18n } from './i18n';
import LanguageSelector from './components/LanguageSelector';
import { DownloadIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ||
                    (process.env.NODE_ENV === 'production' ? 'https://api.aitools.syncode.it' : 'http://localhost:5000');

const INSTAGRAM_CAPTION = "Check out this AI-generated perspective view from AI Craft Studio! What would you see from this arrow's point of view? #AICraftStudio #AI #ArrowView #Perspective #GEMINI";

// Shared state for every signed-in screen, handed down through the router outlet.
function useAppContext() {
  return useOutletContext();
}

// Credits start as null ("not loaded yet"); only a real number means out of credits.
function isOutOfCredits(credits) {
  return typeof credits === 'number' && credits <= 0;
}

function AppShellSkeleton() {
  return (
    <div className="app-skeleton" aria-busy="true" aria-label="Loading">
      <div className="app-skeleton-block app-skeleton-title" />
      <div className="app-skeleton-grid">
        <div className="app-skeleton-block app-skeleton-card" />
        <div className="app-skeleton-block app-skeleton-card" />
        <div className="app-skeleton-block app-skeleton-card" />
      </div>
    </div>
  );
}

function SignedOutLanding({ children }) {
  const { t } = useI18n();
  return (
    <div className="auth-required">
      {children}
      <div className="features-info">
        <p className="eyebrow">{t('app.name')}</p>
        <h2>{t('landing.headline')}</h2>
        <p>{t('landing.sub')}</p>
        <ul className="landing-tools">
          <li>
            <span className="eyebrow">{t('tool.arrow.transform')}</span>
            <span>{t('tool.arrow.desc')}</span>
          </li>
          <li>
            <span className="eyebrow">{t('tool.person.transform')}</span>
            <span>{t('tool.person.desc')}</span>
          </li>
          <li>
            <span className="eyebrow">{t('tool.clothing.transform')}</span>
            <span>{t('tool.clothing.desc')}</span>
          </li>
        </ul>
        <p className="landing-note">{t('landing.note')}</p>
      </div>
    </div>
  );
}

// Everything below a tool: history plus the buy-credits prompt.
function ToolExtras() {
  const { t } = useI18n();
  const { user, getAuthHeaders, backendUrl, imageListRefresh, setShowPayment } = useAppContext();
  return (
    <>
      <ImageHistory
        user={user}
        getAuthHeaders={getAuthHeaders}
        backendUrl={backendUrl}
        refreshTrigger={imageListRefresh}
      />

      <div className="purchase-credits">
        <button
          className="btn btn-primary"
          onClick={() => setShowPayment(true)}
        >
          {t('credits.buyMore')}
        </button>
      </div>
    </>
  );
}

// Main authenticated layout: header, footer, payment modal and the auth gate.
function AppLayout() {
  const navigate = useNavigate();
  const { notifyWarning, notifyError, notifySuccess, notifyApiError } = useNotifications();
  const { t } = useI18n();
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [credits, setCredits] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [imageListRefresh, setImageListRefresh] = useState(0);

  // One subscription for the whole app - nothing renders until Firebase has answered once.
  useEffect(() => {
    // Dev-only: ?preview=1 renders any page as a signed-in user so the UI can
    // be looked at without logging in. Compiled out of production builds - see
    // src/devPreview.js.
    if (isPreviewMode()) {
      setUser(previewUser);
      setCredits(previewCredits);
      setAuthResolved(true);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthResolved(true);
      if (!nextUser) {
        setCredits(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const getAuthHeaders = useCallback(async () => {
    if (!user) return {};

    try {
      const token = await user.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  }, [user]);

  const fetchCredits = useCallback(async () => {
    if (!user) return;
    // Preview mode has no real token, so skip the call rather than render an error.
    if (isPreviewMode()) {
      setCredits(previewCredits);
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${BACKEND_URL}/api/credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Error fetching credits:', error);
      if (error.response?.status !== 401) {
        notifyWarning('Could not load your credit balance. Some actions may be unavailable.');
      }
    }
  }, [user, notifyWarning]);

  useEffect(() => {
    if (user) {
      fetchCredits();
    }
  }, [user, fetchCredits]);

  const handlePaymentSuccess = useCallback(() => {
    fetchCredits();
    setShowPayment(false);
  }, [fetchCredits]);

  const context = useMemo(() => ({
    user,
    credits,
    setCredits,
    fetchCredits,
    getAuthHeaders,
    backendUrl: BACKEND_URL,
    imageListRefresh,
    setImageListRefresh,
    setShowPayment,
    notifyWarning,
    notifyError,
    notifySuccess,
    notifyApiError
  }), [user, credits, fetchCredits, getAuthHeaders, imageListRefresh,
       notifyWarning, notifyError, notifySuccess, notifyApiError]);

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <div
            className="app-brand clickable"
            onClick={() => navigate('/')}
            title={t('nav.backToDashboard')}
          >
            <img src="/logo.svg" alt={t('app.name')} className="app-logo" />
            <h1>{t('app.name')}</h1>
          </div>
        </div>
        <div className="header-right">
          <LanguageSelector />
          {user && <CreditDisplay credits={credits} onClick={() => setShowPayment(true)} />}
          <AuthComponent user={user} authResolved={authResolved} compact={true} />
        </div>
      </header>

      <main className="App-main">
        {!authResolved ? (
          <AppShellSkeleton />
        ) : user ? (
          <Outlet context={context} />
        ) : (
          <SignedOutLanding>
            <AuthComponent user={user} authResolved={authResolved} />
          </SignedOutLanding>
        )}
      </main>

      <footer className="App-footer">
        <div className="footer-links">
          <Link to="/privacy" className="footer-link">{t('footer.privacy')}</Link>
        </div>
      </footer>

      {showPayment && user && (
        <PaymentModal
          onSuccess={handlePaymentSuccess}
          onClose={() => setShowPayment(false)}
          user={user}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  return <Dashboard onSelectFeature={(featureId) => navigate(`/${featureId}`)} />;
}

function ArrowVisionPage() {
  const navigate = useNavigate();
  const {
    user, credits, setCredits, getAuthHeaders, backendUrl,
    setImageListRefresh, setShowPayment,
    notifyWarning, notifyError, notifySuccess, notifyApiError
  } = useAppContext();

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [instagramCaption, setInstagramCaption] = useState(null);
  const resultRef = useScrollIntoView(generatedResult);

  const handleImageGeneration = async (imageBlob, arrowLocation) => {
    if (!user) {
      notifyWarning('Please sign in to generate images.');
      return;
    }

    if (isOutOfCredits(credits)) {
      setShowPayment(true);
      return;
    }

    setIsGenerating(true);
    setGeneratedResult(null);
    try {
      const formData = new FormData();
      formData.append('image', imageBlob, 'map-screenshot.png');

      if (arrowLocation) {
        formData.append('location', JSON.stringify(arrowLocation));
      }

      const authHeaders = await getAuthHeaders();
      const response = await axios.post(`${backendUrl}/api/generate-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...authHeaders
        },
      });

      setGeneratedResult(response.data.result);
      setCredits(response.data.creditsRemaining);
      setImageListRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Error generating image:', error);
      const described = notifyApiError(error, 'Could not generate that image. Please try again.');
      // 400 and 402 are both used for insufficient credits today; the code is the durable signal.
      if (described.code === 'INSUFFICIENT_CREDITS' || described.status === 402 || described.status === 400) {
        setShowPayment(true);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = (imageData, filename = 'arrow-view.png') => {
    if (!imageData || !imageData.startsWith('data:image')) {
      notifyError('No image data available to download.');
      return;
    }

    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadImageForSharing = async (imageData) => {
    const authHeaders = await getAuthHeaders();
    const response = await axios.post(`${backendUrl}/api/upload-for-sharing`,
      { imageData },
      { headers: authHeaders }
    );
    return response.data.publicUrl;
  };

  const shareToTwitter = async (imageData) => {
    try {
      const imageUrl = await uploadImageForSharing(imageData);

      const text = "Check out this AI-generated perspective view from AI Craft Studio! What would you see from this arrow's point of view?";
      const hashtags = "AICraftStudio,AI,ArrowView,Perspective,GEMINI";

      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(imageUrl)}&hashtags=${hashtags}`;
      window.open(twitterUrl, '_blank', 'width=600,height=400');
    } catch (error) {
      console.error('Twitter sharing error:', error);
      notifyApiError(error, 'Could not prepare the image for Twitter. Please try again.');
    }
  };

  const shareToFacebook = async (imageData) => {
    try {
      const imageUrl = await uploadImageForSharing(imageData);

      const text = "Amazing AI-generated perspective view from AI Craft Studio! See what the red arrow sees using Google Maps + GEMINI AI.";

      if (window.FB && window.FB.ui) {
        window.FB.ui({
          method: 'share',
          href: imageUrl,
          quote: text
        });
      } else {
        const simpleFbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(imageUrl)}&t=${encodeURIComponent(text)}`;
        window.open(simpleFbUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      }
    } catch (error) {
      console.error('Facebook sharing error:', error);
      notifyApiError(error, 'Could not prepare the image for Facebook. Please try again.');
    }
  };

  const shareToInstagram = async (imageData) => {
    try {
      if (navigator.share && navigator.canShare) {
        try {
          const imageUrl = await uploadImageForSharing(imageData);

          const shareData = {
            title: 'AI-Generated Arrow Vision',
            text: INSTAGRAM_CAPTION,
            url: imageUrl
          };

          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (shareError) {
          console.log('Web Share API failed, falling back to download method:', shareError);
        }
      }

      downloadImage(imageData, `arrow-view-${Date.now()}.png`);

      let copied = false;
      try {
        await navigator.clipboard.writeText(INSTAGRAM_CAPTION);
        copied = true;
      } catch (clipboardError) {
        copied = false;
      }

      setInstagramCaption({ text: INSTAGRAM_CAPTION, copied });
      notifySuccess(copied
        ? 'Image downloaded and caption copied.'
        : 'Image downloaded. Copy the caption below for Instagram.');
    } catch (error) {
      console.error('Instagram sharing error:', error);
      notifyApiError(error, 'Could not prepare the Instagram share. Please try again.');
    }
  };

  return (
    <>
      <MapComponent
        onImageGeneration={handleImageGeneration}
        isGenerating={isGenerating}
        onBack={() => navigate('/')}
      />

      {isGenerating && (
        <GenerationProgress
          title="Generating the arrow's point of view"
          estimateSeconds={25}
          steps={[
            'Capturing the map...',
            'Sending the scene to the AI model...',
            'Imagining the view from the arrow...',
            'Rendering the final image...'
          ]}
        />
      )}

      {generatedResult && (
        <div className="result-container" ref={resultRef}>
          <h3>What the Arrow Sees:</h3>
          {generatedResult.startsWith('data:image') ? (
            <>
              <img
                src={generatedResult}
                alt="Generated perspective view"
              />
              <div className="action-buttons" style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => downloadImage(generatedResult, `arrow-view-${Date.now()}.png`)}
                  style={{ marginRight: '10px' }}
                >
                  <DownloadIcon style={{ marginRight: '6px' }} />
                  Download
                </button>

                <div className="share-buttons" style={{ display: 'inline-block', marginLeft: '10px' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => shareToTwitter(generatedResult)}
                    style={{ marginRight: '8px', backgroundColor: '#000000' }}
                    title="Share on Twitter"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={() => shareToFacebook(generatedResult)}
                    style={{ marginRight: '8px', backgroundColor: '#1877F2' }}
                    title="Share on Facebook"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                      <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={() => shareToInstagram(generatedResult)}
                    style={{ backgroundColor: '#E4405F' }}
                    title="Copy caption for Instagram"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
                      <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </button>
                </div>
              </div>

              {instagramCaption && (
                <InstagramShareTips
                  caption={instagramCaption.text}
                  copied={instagramCaption.copied}
                  onClose={() => setInstagramCaption(null)}
                />
              )}
            </>
          ) : (
            <p>{generatedResult}</p>
          )}
        </div>
      )}

      <ToolExtras />
    </>
  );
}

function PersonRemoverPage() {
  const navigate = useNavigate();
  const context = useAppContext();
  return (
    <>
      <PersonRemover
        user={context.user}
        credits={context.credits}
        setCredits={context.setCredits}
        getAuthHeaders={context.getAuthHeaders}
        backendUrl={context.backendUrl}
        onBack={() => navigate('/')}
        setImageListRefresh={context.setImageListRefresh}
        setShowPayment={context.setShowPayment}
      />
      <ToolExtras />
    </>
  );
}

function ClothingTryOnPage() {
  const navigate = useNavigate();
  const context = useAppContext();
  return (
    <>
      <ClothingTryOn
        user={context.user}
        credits={context.credits}
        setCredits={context.setCredits}
        getAuthHeaders={context.getAuthHeaders}
        backendUrl={context.backendUrl}
        onBack={() => navigate('/')}
        setImageListRefresh={context.setImageListRefresh}
        setShowPayment={context.setShowPayment}
      />
      <ToolExtras />
    </>
  );
}

function PaymentCompletePage() {
  const { backendUrl, getAuthHeaders, fetchCredits } = useAppContext();
  return (
    <PaymentComplete
      backendUrl={backendUrl}
      getAuthHeaders={getAuthHeaders}
      onCreditsGranted={fetchCredits}
    />
  );
}

// Privacy Page Wrapper
function PrivacyPage() {
  const navigate = useNavigate();
  return <PrivacyPolicy onBack={() => navigate('/')} />;
}

// Admin Page Wrapper
function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  // Memoized because AdminPanel lists it as an effect dependency; an unstable
  // identity here re-fires the panel's data fetches on every render.
  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  }, []);

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} backendUrl={BACKEND_URL} onBack={() => navigate('/')} />;
  }

  return <AdminPanel onLogout={handleLogout} backendUrl={BACKEND_URL} onBack={() => navigate('/')} />;
}

// Any URL that matches nothing else - used to render a blank white page.
function NotFoundPage() {
  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <Link to="/" className="app-brand clickable" title="Return to Dashboard">
            <img src="/logo.svg" alt="AI Craft Studio" className="app-logo" />
            <h1>AI Craft Studio</h1>
          </Link>
        </div>
      </header>
      <main className="App-main">
        <NotFound />
      </main>
    </div>
  );
}

// Main App with Router
function App() {
  return (
    <Router>
      <NotificationProvider>
        <ErrorBoundary>
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/arrow-vision" element={<ArrowVisionPage />} />
              <Route path="/person-remover" element={<PersonRemoverPage />} />
              <Route path="/clothing-tryon" element={<ClothingTryOnPage />} />
              <Route path="/payment-complete" element={<PaymentCompletePage />} />
            </Route>
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ErrorBoundary>
      </NotificationProvider>
    </Router>
  );
}

export default App;
